/**
 * Order/Sales data models.
 */

import { BaseEntity, TimestampMixin, SearchableEntity } from './base';
import type { ProfitPlus2k8SaleIntegration } from './integrations/profitPlus2k8';

export interface OrderProductProfitPlus2k8Integration {
  fields?: {
    /** Sub-almacén (dbo.sub_alma.co_sub) used for this order line. */
    co_sub?: string;
    /** Alias accepted by bridge/sql path for reng_ped.co_alma (sub code). */
    co_alma?: string;
    [key: string]: unknown;
  };
  syncedAt?: Date | null;
  [key: string]: unknown;
}

export interface OrderProduct {
  productId: string;
  code: string;
  name: string;
  brand?: string;
  description?: string;
  price: number;
  quantity: number;
  /** Unit cost snapshot at sale time (pre-tax COGS basis). */
  cost?: number;
  /** Optional line discount percentage (0-100), exported to Profit `reng_ped.porc_desc`. */
  discount?: number;
  imageUrl?: string;
  taxId?: string;
  taxRate?: number;
  /** Integration-scoped line metadata (avoid top-level ERP-specific fields). */
  profitPlus2k8?: OrderProductProfitPlus2k8Integration;
  /** Fulfillment warehouse for this line when multi-warehouse inventory is enabled. */
  warehouseId?: string;
}

export interface OrderBase extends SearchableEntity {
  clientId: string;
  clientName: string;
  deliveryAddress?: string; // Delivery address captured at sale time
  invoiced?: boolean; // Whether sale has been manually invoiced
  creatorSalesPerson: string; // Creator sales person email
  paymentMethod: string; // Payment method name
  paymentCurrency?: string; // Currency used for payment (currency or referenceCurrency)
  creditTermsDays?: number | null;
  dueDate?: Date | null;
  subtotal: number;
  ivaAmount?: number; // IVA amount for transparency
  total: number;
  /** Pre-tax COGS: sum of line cost × quantity. */
  costSubtotal?: number;
  /** Pre-tax gross profit: subtotal − costSubtotal. */
  grossProfit?: number;
  exchangeRate?: number; // USD exchange rate used at time of order
  deleted?: boolean; // Soft delete flag
  comment?: string;
  /** Fulfillment warehouse when multi-warehouse inventory is enabled. */
  warehouseId?: string;
  /**
   * True when pending-sale stock is held in balance.reserved (multi-warehouse
   * + approval). Distinct from approvalState: single-WH pending may not reserve.
   * Server-managed; not set by the client on create/edit.
   */
  stockReserved?: boolean | null;
  /**
   * Sale-level discount % (0–100), applied after line discounts.
   * Multiplicative with products[].discount; does not affect inventory.
   */
  orderDiscountPercent?: number | null;
  products: OrderProduct[];

  /** Profit Plus 2k8 (ERP): one field per integration – native fields (dbo.pedidos) + syncedAt. */
  profitPlus2k8?: ProfitPlus2k8SaleIntegration;
}

export interface Order extends OrderBase, BaseEntity, TimestampMixin {
  approvalState: 'approved' | 'pending' | 'rejected';
  saleNumber?: number;
  /**
   * Wall-clock insert time (ADR-0002). Absent on legacy docs ⇒ treat as `created`.
   */
  recordedAt?: Date;
}

export interface OrderCreate extends Omit<OrderBase, 'keywords'> {
  /**
   * Business/emission date. Defaults to now on the server when omitted.
   * May be in the past (admin late entry).
   */
  created?: Date;
}

export interface OrderUpdate extends Partial<OrderBase> {
  approvalState?: 'approved' | 'pending' | 'rejected';
  updated?: Date;
}

/** Approved-sale-only: merge Profit export blobs (server route `profit-export-repair`). */
export interface OrderProfitPlusExportRepair {
  profitPlus2k8?: ProfitPlus2k8SaleIntegration;
  products?: Array<{
    productId: string;
    profitPlus2k8?: OrderProductProfitPlus2k8Integration;
  }>;
}

// Default values for orders
export const ORDER_DEFAULTS = {
  approvalState: 'pending' as const,
  subtotal: 0,
  total: 0,
  products: [],
} as const;

// Order search and filter types
export interface OrderFilters {
  searchTerm?: string;
  clientId?: string;
  clientName?: string;
  creatorSalesPerson?: string;
  approvalState?: 'approved' | 'pending' | 'rejected';
  dateFrom?: Date;
  dateTo?: Date;
  minTotal?: number;
  maxTotal?: number;
}

export interface OrderQueryResult {
  orders: Order[];
  lastDoc?: any; // Firestore document snapshot
  hasMore: boolean;
  totalCount?: number;
}

/** @deprecated Use SalesPeriodReport from `/sales/{orgId}/report` instead. */
export interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  pendingOrders: number;
  approvedOrders: number;
  rejectedOrders: number;
  /** Pre-tax net revenue (subtotal sum). */
  netRevenue?: number;
  /** Pre-tax COGS. */
  totalCost?: number;
  /** Pre-tax gross profit. */
  grossProfit?: number;
  /** Pre-tax gross margin %. */
  grossMarginPercent?: number;
}

// Payment method constants
export const PAYMENT_METHODS = {
  CASH: 1,
  CREDIT: 2,
  TRANSFER: 3,
  CHECK: 4,
} as const;

export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHODS.CASH]: 'Cash',
  [PAYMENT_METHODS.CREDIT]: 'Credit',
  [PAYMENT_METHODS.TRANSFER]: 'Bank Transfer',
  [PAYMENT_METHODS.CHECK]: 'Check',
} as const;
